/* Created by Interactive 63 - https://interactive63.com */
/**
 * CSS generation: one rule per animated element holding its animation list and static values,
 * plus a @keyframes block per multi-keyframe track. Every selector is scoped to the scene class
 * so several exported scenes can share a page.
 */
import { easingToCss } from '../easing/to-css';
import { formatNumber } from '../easing/format-number';
import type { Keyframe, Track } from '../model/types';
import { PROPERTIES } from '../properties/registry';
import { effectiveKeyframes, repeatPeriod } from '../timeline/repeat';
import { cssDeclarations, kebab, num } from './values';
import type { AnimatedElement, CompileWarning, SceneTiming } from './types';

export interface CssScene {
  sceneName: string;
  sceneClass: string;
}

/** Formats a keyframe time as a percentage of the duration. */
export function percent(time: number, duration: number): string {
  return `${formatNumber((time / duration) * 100, 3)}%`;
}

/** Returns the name of the @keyframes block for a track. */
function keyframesName(scene: CssScene, id: string, track: Track): string {
  return `st-${scene.sceneName}-${id}-${kebab(track.property)}`;
}

/** Formats the iteration count for the animation shorthand. */
function iterationText(iterations: number | 'infinite'): string {
  return iterations === 'infinite' ? 'infinite' : num(iterations);
}

/** Writes an indented block of declarations. */
function block(selector: string, declarations: string[], indent = ''): string {
  const body = declarations.map((d) => `${indent}  ${d};`).join('\n');
  return `${indent}${selector} {\n${body}\n${indent}}`;
}

/** Builds the @keyframes block for a track with two or more keyframes. */
function keyframesBlock(
  name: string,
  track: Track,
  keyframes: readonly Keyframe[],
  duration: number,
  pathLength: number | undefined,
): string {
  const first = keyframes[0];
  const last = keyframes[keyframes.length - 1];
  if (!first || !last) return '';
  const blocks: string[] = [];

  /** Declarations for one keyframe, with its segment easing unless it is the last. */
  const declarations = (kf: Keyframe, withEasing: boolean): string[] => {
    const lines = cssDeclarations(track.property, kf.value, { pathLength });
    if (withEasing) {
      const easing = easingToCss(kf.easing);
      if (easing.fallback) lines.push(`animation-timing-function: ${easing.fallback}`);
      lines.push(`animation-timing-function: ${easing.value}`);
    }
    return lines;
  };

  if (first.time > 0) {
    blocks.push(
      block(
        '0%',
        [
          ...cssDeclarations(track.property, first.value, { pathLength }),
          'animation-timing-function: linear',
        ],
        '  ',
      ),
    );
  }
  keyframes.forEach((kf, index) => {
    blocks.push(
      block(percent(kf.time, duration), declarations(kf, index < keyframes.length - 1), '  '),
    );
  });
  if (last.time < duration) {
    blocks.push(block('100%', cssDeclarations(track.property, last.value, { pathLength }), '  '));
  }
  return `@keyframes ${name} {\n${blocks.join('\n')}\n}`;
}

/**
 * The animation shorthand tail for a track. Tracks that play once get their own iteration count,
 * direction and a fill mode that holds the end value, so they do not restart when the scene loops.
 */
export function trackTiming(track: Track, timing: SceneTiming): string {
  const duration = `${num(timing.duration)}ms linear ${num(timing.delay)}ms`;
  if (track.restartWithScene !== false) {
    return `${duration} ${iterationText(timing.iterations)} ${timing.direction} ${timing.fillMode}`;
  }
  const fill =
    timing.fillMode === 'none'
      ? 'forwards'
      : timing.fillMode === 'backwards'
        ? 'both'
        : timing.fillMode;
  return `${duration} 1 normal ${fill}`;
}

/** Warnings about a repeating track: cut-short expansion and a last cycle that is cut off. */
function repeatWarnings(
  track: Track,
  capped: boolean,
  duration: number,
  label: string,
  elementId: string,
): CompileWarning[] {
  const warnings: CompileWarning[] = [];
  const name = `${label} ${PROPERTIES[track.property].label}`;
  if (capped) {
    warnings.push({
      code: 'repeat-capped',
      message: `The repeat on ${name} was cut short because it has too many cycles.`,
      elementId,
    });
  }
  const period = repeatPeriod(track);
  const repeat = track.repeat;
  if (repeat && period !== undefined) {
    const runsToEnd = repeat.count === 'infinite' || repeat.count * period > duration;
    const remainder = duration % period;
    if (runsToEnd && remainder > 0.5 && period - remainder > 0.5) {
      warnings.push({
        code: 'repeat-partial-cycle',
        message: `The last repeat of ${name} is cut off at the end of the scene.`,
        elementId,
      });
    }
  }
  return warnings;
}

export interface ElementGroup {
  selector: string;
  tracks: Track[];
  origin: boolean;
}

/**
 * Splits an element's tracks into the rules that style them. A wrapped element has its transform
 * tracks on the wrapper and everything else on the element itself.
 */
export function elementGroups(element: AnimatedElement, scene: CssScene): ElementGroup[] {
  const { id } = element;
  if (!element.wrapped) {
    return [{ selector: `.${scene.sceneClass} .st-${id}`, tracks: element.tracks, origin: true }];
  }
  return [
    {
      selector: `.${scene.sceneClass} .st-${id}`,
      tracks: element.tracks.filter((t) => PROPERTIES[t.property].usesTransform),
      origin: true,
    },
    {
      selector: `.${scene.sceneClass} .st-${id}-self`,
      tracks: element.tracks.filter((t) => !PROPERTIES[t.property].usesTransform),
      origin: false,
    },
  ];
}

/** Whether a track produces a running animation, rather than a constant. */
export function isAnimatedTrack(track: Track): boolean {
  return track.keyframes.length > 1;
}

/**
 * Generates the CSS for all animated elements. A CSS-only shape morph runs on the same element
 * as its other animations, and an element can only have one `animation` list, so the morph's
 * entry is passed in and written into that list. The result names the elements it was merged
 * for, which then need no rule of their own.
 */
export function buildCss(
  animated: readonly AnimatedElement[],
  timing: SceneTiming,
  scene: CssScene,
  morphEntries: ReadonlyMap<string, string> = new Map(),
): {
  css: string;
  warnings: CompileWarning[];
  animatedSelectors: string[];
  mergedMorphs: Set<string>;
} {
  const mergedMorphs = new Set<string>();
  const animatedSelectors: string[] = [];
  const warnings: CompileWarning[] = [];
  const sections: string[] = [];

  for (const element of animated) {
    const { id, meta } = element;
    const pathLength = meta.pathLength;
    if (element.tracks.some((t) => t.property === 'draw') && pathLength === undefined) {
      warnings.push({
        code: 'draw-length',
        message: `Stroke reveal length could not be measured for ${meta.label}`,
        elementId: id,
      });
    }

    for (const group of elementGroups(element, scene)) {
      if (group.tracks.length === 0) continue;
      const declarations: string[] = [];
      if (group.origin && group.tracks.some((t) => PROPERTIES[t.property].usesTransform)) {
        declarations.push('transform-box: fill-box');
        declarations.push(`transform-origin: ${num(meta.origin.x)}% ${num(meta.origin.y)}%`);
      }
      const animations: string[] = [];
      const keyframeBlocks: string[] = [];
      const statics: string[] = [];
      for (const track of group.tracks) {
        const first = track.keyframes[0];
        if (!first) continue;
        if (track.keyframes.length === 1) {
          statics.push(...cssDeclarations(track.property, first.value, { pathLength }));
          continue;
        }
        const name = keyframesName(scene, id, track);
        const expanded = effectiveKeyframes(track, timing.duration);
        warnings.push(...repeatWarnings(track, expanded.capped, timing.duration, meta.label, id));
        animations.push(`${name} ${trackTiming(track, timing)}`);
        keyframeBlocks.push(
          keyframesBlock(name, track, expanded.keyframes, timing.duration, pathLength),
        );
      }
      // The morph runs on the element itself, which is the wrapper-free group.
      const morphEntry = !element.wrapped || !group.origin ? morphEntries.get(id) : undefined;
      if (morphEntry !== undefined && animations.length > 0) {
        animations.push(morphEntry);
        mergedMorphs.add(id);
      }
      if (animations.length > 0) animatedSelectors.push(group.selector);
      if (animations.length > 0) declarations.push(`animation:\n    ${animations.join(',\n    ')}`);
      declarations.push(...statics);
      if (declarations.length > 0) sections.push(block(group.selector, declarations));
      sections.push(...keyframeBlocks);
    }
  }
  return {
    css: sections.join('\n\n') + (sections.length > 0 ? '\n' : ''),
    warnings,
    animatedSelectors,
    mergedMorphs,
  };
}
